import { ref, type Ref } from 'vue';
import type { EcxTimeFormat } from '@edgeconnex/design-system';
import { type IAbstractPeriodicTask, AbstractPeriodicTask } from '@/generics/AbstractPeriodicTask';
import userPreferenceAPI from '@/apiServices/userPreferenceAPI';
import { PM_CALENDAR_GRID_ID } from '@/components/pm-calendar/gridPreferenceId';
import { SESSION_GRIDS_KEY } from '@/services/sessionStorageKeys';

//=================================================================================================
// Per-user UI preferences, split by lifetime (PR-797):
//  - General preferences (color scheme, time format, timezone, dashboardSeeded, preferredSites)
//    are server-side and survive across sessions and devices. Flushed on route-leave, on a
//    60-second snapshot, and on logout; the immediate-save setters flush right away.
//  - Grid state (filters, sort, rows-per-page, page) is session-scoped: held in sessionStorage,
//    which matches the user-session lifetime exactly (the session token lives there too) —
//    survives a reload while signed in, dies with the tab, and is cleared explicitly on logout,
//    inactivity timeout, and tenant switch.
//=================================================================================================

// v3: grid state moved to session scope (PR-797) — a <3 blob's grids are stripped after the
// preferredSites seed reads them. v2: work-order `personnel` filter changed shape (now moot).
export const PREFERENCES_SCHEMA_VERSION = 3;

export type ColorScheme = 'light' | 'dark';
/** Stored theme choice — 'system' resolves to a ColorScheme via prefers-color-scheme (useTheme). */
export type ThemePreference = ColorScheme | 'system';
export type SystemOfMeasurement = 'metric' | 'imperial';
export type TemperatureUnit = 'celsius' | 'fahrenheit';

export const DEFAULT_TIME_FORMAT: EcxTimeFormat = '24';

export interface GridSort {
  field: string | null;
  order: number;
}

export interface GridPreference {
  rowsPerPage?: number;
  sort?: GridSort;
  filters?: Record<string, unknown>;
  page?: number;
}

export interface GeneralPreference {
  colorScheme?: ThemePreference;
  timeFormat?: EcxTimeFormat;
  timeZone?: string;
  /** True once the starter dashboard has been seeded — an emptied dashboard stays empty. */
  dashboardSeeded?: boolean;
  /** Last viewed dashboard; resolved client-side against the visible list. */
  defaultDashboardId?: number;
  /** Explicit preferred sites (PR-770); undefined = never set, [] = deliberately none. */
  preferredSites?: string[];
  systemOfMeasurement?: SystemOfMeasurement;
  temperatureUnit?: TemperatureUnit;
  numberLocale?: string;
}

export interface UserPreferences {
  version: number;
  general: GeneralPreference;
  grids: Record<string, GridPreference>;
}

function emptyPreferences(): UserPreferences {
  return { version: PREFERENCES_SCHEMA_VERSION, general: {}, grids: {} };
}

//=================================================================================================
export interface IUserPreferencesService extends IAbstractPeriodicTask {
  prefs: Ref<UserPreferences>;
  load(): Promise<void>;
  flush(): Promise<void>;
  getGrid(gridId: string): GridPreference | undefined;
  setGrid(gridId: string, value: GridPreference): void;
  clearGrids(): void;
  getColorScheme(): ThemePreference | undefined;
  setColorScheme(scheme: ThemePreference): void;
  getPreferredSites(): string[] | undefined;
  setPreferredSites(sites: string[]): void;
  getTimeFormat(): EcxTimeFormat;
  setTimeFormat(format: EcxTimeFormat): void;
  getTimeZone(): string | undefined;
  setTimeZone(timeZone: string): void;
  getSystemOfMeasurement(): SystemOfMeasurement | undefined;
  setSystemOfMeasurement(system: SystemOfMeasurement): void;
  getTemperatureUnit(): TemperatureUnit | undefined;
  setTemperatureUnit(unit: TemperatureUnit): void;
  getNumberLocale(): string | undefined;
  setNumberLocale(locale: string): void;
  getDashboardSeeded(): boolean;
  setDashboardSeeded(): void;
  getDefaultDashboardId(): number | undefined;
  setDefaultDashboardId(dashboardId: number): void;
  reset(): void;
}
//=================================================================================================
class UserPreferencesServiceClass extends AbstractPeriodicTask implements IUserPreferencesService {
  public prefs: Ref<UserPreferences> = ref(emptyPreferences());
  private dirty = false;
  private changedGeneralKeys = new Set<keyof GeneralPreference>();
  private inFlight: Promise<void> | null = null;

  // Singleton instance — periodic run() is the 60s "snapshot while lingering" flush.
  static instance: UserPreferencesServiceClass = new UserPreferencesServiceClass(60);

  //-----------------------------------------------------------------------------------------------
  private constructor(refreshInterval: number = 60) {
    super(refreshInterval);
    // AbstractPeriodicTask's constructor starts the timer immediately (at module-load time). We
    // don't want a 60s snapshot ticking on the login screen, so stop it here — load() starts it
    // once a user is actually logged in, and reset() stops it again on logout.
    this.stop();
  }
  //-----------------------------------------------------------------------------------------------
  public static getInstance(): UserPreferencesServiceClass {
    return UserPreferencesServiceClass.instance;
  }
  //-----------------------------------------------------------------------------------------------
  // Load the user's preferences from the server. Tolerant of a missing/empty/older blob.
  public async load(): Promise<void> {
    let stored: UserPreferences | null | undefined;
    try {
      const response = await userPreferenceAPI.get();
      stored = response.data;
      this.prefs.value = this.normalize(stored);
    } catch (err) {
      console.error('UserPreferencesService.load failed', err);
      this.prefs.value = emptyPreferences();
    }
    this.dirty = false;
    this.changedGeneralKeys.clear();

    // One-time migration: seed the cross-device color scheme from the per-device localStorage
    // value set by the legacy theme toggle, so existing users keep their current theme. Guarded by
    // a one-shot flag — without it, a stale localStorage.theme left on a shared machine would be
    // re-migrated into the next user's freshly-loaded (scheme-less) preferences on every load.
    const ls =
      typeof window !== 'undefined' && typeof window.localStorage?.getItem === 'function'
        ? window.localStorage
        : null;
    if (ls && !this.prefs.value.general.colorScheme && !ls.getItem('themeMigrated')) {
      const storedTheme = ls.getItem('theme');
      if (storedTheme === 'dark' || storedTheme === 'light') {
        this.prefs.value.general.colorScheme = storedTheme;
        this.changedGeneralKeys.add('colorScheme');
        this.dirty = true;
      }
      ls.setItem('themeMigrated', '1');
    }

    // One-time seed of the explicit preferred-sites preference (PR-770) from its former
    // interim source, the PM Calendar's persisted site filter. The field being set is the
    // migration marker — [] means the user deliberately cleared it, so never re-seed. Must run
    // before the v3 strip below discards the server-blob grids it reads from.
    if (this.prefs.value.general.preferredSites === undefined) {
      const calendarSites = this.prefs.value.grids[PM_CALENDAR_GRID_ID]?.filters?.sites;
      if (Array.isArray(calendarSites) && calendarSites.length) {
        this.prefs.value.general.preferredSites = calendarSites.filter(
          (s): s is string => typeof s === 'string',
        );
        this.changedGeneralKeys.add('preferredSites');
        this.dirty = true;
      }
    }

    // v3 strip (PR-797): grid state is session-scoped now — legacy server-blob grids must never
    // preload again. Marking dirty makes the next flush persist the versioned, grid-less blob.
    // Only for a blob we actually received: marking dirty after a failed GET would flush empty
    // preferences over the user's real ones.
    if (stored && (typeof stored.version !== 'number' || stored.version < PREFERENCES_SCHEMA_VERSION)) {
      this.dirty = true;
    }
    // The working grids copy is the session's own state, never the server's.
    this.prefs.value.grids = this.readSessionGrids();

    // Logged in now — start the 60s snapshot timer (stopped again by reset() on logout).
    this.start();
  }
  //-----------------------------------------------------------------------------------------------
  // Persist the working copy if it has unsaved changes and the user is still logged in.
  public async flush(): Promise<void> {
    // Single-flight: route-leave, the 60s snapshot and setColorScheme's immediate save can all
    // call flush() concurrently. Wait for any in-flight PUT to finish first so they can't overlap
    // or apply out of order, then re-evaluate the dirty flag against the latest working copy.
    while (this.inFlight) {
      await this.inFlight;
    }
    if (!this.dirty) {
      return;
    }
    if (!sessionStorage.getItem('sessionToken')) {
      return; // not logged in — nothing to persist against
    }

    this.dirty = false;
    const changedKeys = [...this.changedGeneralKeys];
    this.changedGeneralKeys.clear();
    this.inFlight = (async () => {
      try {
        const latest = await userPreferenceAPI.get();
        const changes = Object.fromEntries(changedKeys.map((key) => [key, this.prefs.value.general[key]]));
        // Grid state is session-scoped (PR-797) — the server blob only carries general preferences.
        await userPreferenceAPI.save({
          version: PREFERENCES_SCHEMA_VERSION,
          general: { ...this.normalize(latest.data).general, ...changes },
          grids: {},
        });
      } catch (err) {
        // Silent by design: flush() runs from the 60s background snapshot, the route-leave guard
        // and setColorScheme — none of which should surface a toast on a transient network blip.
        // We log and re-mark dirty so the next flush / snapshot retries. Do NOT "fix" this into a
        // user-facing toast on the background path (see PR #1443 review).
        console.error('UserPreferencesService.flush failed', err);
        changedKeys.forEach((key) => this.changedGeneralKeys.add(key));
        this.dirty = true; // retry on the next flush / snapshot
      } finally {
        this.inFlight = null;
      }
    })();
    await this.inFlight;
  }
  //-----------------------------------------------------------------------------------------------
  public getGrid(gridId: string): GridPreference | undefined {
    return this.prefs.value.grids[gridId];
  }
  //-----------------------------------------------------------------------------------------------
  public setGrid(gridId: string, value: GridPreference): void {
    this.prefs.value.grids[gridId] = value;
    this.writeSessionGrids();
  }
  //-----------------------------------------------------------------------------------------------
  // Grid state (filters, search, sort, paging) is tenant-context UI: it is cleared when the user
  // switches their working tenant, so one tenant's filters (e.g. a site search) can't silently
  // empty every list in another. General preferences (theme, time format) survive.
  public clearGrids(): void {
    this.prefs.value.grids = {};
    this.removeSessionGrids();
  }
  //-----------------------------------------------------------------------------------------------
  public getColorScheme(): ThemePreference | undefined {
    return this.prefs.value.general.colorScheme;
  }
  //-----------------------------------------------------------------------------------------------
  // Color scheme saves immediately — a user toggling the theme then closing the tab without
  // navigating should not lose it.
  public setColorScheme(scheme: ThemePreference): void {
    this.setGeneral('colorScheme', scheme);
  }
  //-----------------------------------------------------------------------------------------------
  public getPreferredSites(): string[] | undefined {
    return this.prefs.value.general.preferredSites;
  }
  //-----------------------------------------------------------------------------------------------
  // Saved immediately, like the other general preferences — set rarely, must survive a tab close.
  public setPreferredSites(sites: string[]): void {
    this.setGeneral('preferredSites', sites);
  }
  //-----------------------------------------------------------------------------------------------
  public getTimeFormat(): EcxTimeFormat {
    return this.prefs.value.general.timeFormat ?? DEFAULT_TIME_FORMAT;
  }
  //-----------------------------------------------------------------------------------------------
  // Time format / timezone are global UI preferences set rarely — save immediately like the color
  // scheme so a change made then a tab-close (without navigating) is not lost.
  public setTimeFormat(format: EcxTimeFormat): void {
    this.setGeneral('timeFormat', format);
  }
  //-----------------------------------------------------------------------------------------------
  public getTimeZone(): string | undefined {
    return this.prefs.value.general.timeZone;
  }
  //-----------------------------------------------------------------------------------------------
  public getDashboardSeeded(): boolean {
    return this.prefs.value.general.dashboardSeeded === true;
  }
  //-----------------------------------------------------------------------------------------------
  // Saved immediately — losing the marker would re-seed an intentionally emptied dashboard.
  public setDashboardSeeded(): void {
    this.setGeneral('dashboardSeeded', true);
  }
  //-----------------------------------------------------------------------------------------------
  public getDefaultDashboardId(): number | undefined {
    return this.prefs.value.general.defaultDashboardId;
  }
  //-----------------------------------------------------------------------------------------------
  public setDefaultDashboardId(dashboardId: number): void {
    this.setGeneral('defaultDashboardId', dashboardId);
  }
  //-----------------------------------------------------------------------------------------------
  public setTimeZone(timeZone: string): void {
    this.setGeneral('timeZone', timeZone);
  }
  //-----------------------------------------------------------------------------------------------
  public getSystemOfMeasurement(): SystemOfMeasurement | undefined {
    return this.prefs.value.general.systemOfMeasurement;
  }
  public setSystemOfMeasurement(system: SystemOfMeasurement): void {
    this.setGeneral('systemOfMeasurement', system);
  }
  public getTemperatureUnit(): TemperatureUnit | undefined {
    return this.prefs.value.general.temperatureUnit;
  }
  public setTemperatureUnit(unit: TemperatureUnit): void {
    this.setGeneral('temperatureUnit', unit);
  }
  public getNumberLocale(): string | undefined {
    return this.prefs.value.general.numberLocale;
  }
  public setNumberLocale(locale: string): void {
    this.setGeneral('numberLocale', locale);
  }
  public reset(): void {
    this.prefs.value = emptyPreferences();
    this.dirty = false;
    this.changedGeneralKeys.clear();
    this.removeSessionGrids();
    this.stop(); // logged out — stop the 60s snapshot timer
  }
  //-----------------------------------------------------------------------------------------------
  private setGeneral<K extends keyof GeneralPreference>(key: K, value: GeneralPreference[K]): void {
    this.prefs.value.general[key] = value;
    this.changedGeneralKeys.add(key);
    this.dirty = true;
    void this.flush();
  }
  // removeItem rather than writing '{}': a quota-full setItem can throw, and the callers (logout,
  // timeout, tenant switch) must not leave the previous context's filters behind.
  private removeSessionGrids(): void {
    try {
      sessionStorage.removeItem(SESSION_GRIDS_KEY);
    } catch (err) {
      console.error('UserPreferencesService: clearing session grid state failed', err);
    }
  }
  //-----------------------------------------------------------------------------------------------
  private readSessionGrids(): Record<string, GridPreference> {
    try {
      const raw = sessionStorage.getItem(SESSION_GRIDS_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, GridPreference>;
      }
    } catch (err) {
      console.error('UserPreferencesService: reading session grid state failed', err);
    }
    return {};
  }
  //-----------------------------------------------------------------------------------------------
  private writeSessionGrids(): void {
    try {
      sessionStorage.setItem(SESSION_GRIDS_KEY, JSON.stringify(this.prefs.value.grids));
    } catch (err) {
      console.error('UserPreferencesService: persisting session grid state failed', err);
    }
  }
  //-----------------------------------------------------------------------------------------------
  private normalize(data: UserPreferences | null | undefined): UserPreferences {
    if (!data || typeof data !== 'object') {
      return emptyPreferences();
    }
    // Keep the shape well-formed regardless of what was stored; unknown keys are harmless and
    // future schema migrations key off `version`.
    return {
      version: PREFERENCES_SCHEMA_VERSION,
      general: data.general && typeof data.general === 'object' ? data.general : {},
      grids: data.grids && typeof data.grids === 'object' ? data.grids : {},
    };
  }
  //-----------------------------------------------------------------------------------------------
  // Implementation of abstract method run() of AbstractPeriodicTask — the 60s snapshot.
  protected async run(): Promise<void> {
    await this.flush();
  }
}
//-----------------------------------------------------------------------------------------------
// Export singleton instance
export const UserPreferencesService: IUserPreferencesService = UserPreferencesServiceClass.getInstance();
// EOF
